import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjArrowLeft, jnjFilter } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { SelectedType } from "@jmc/solid-design-system/src/components/atoms/Select/Select";
import { FilterSelectOptionType } from "@jmc/solid-design-system/src/components/atoms/Select/Select.Helpers";
import { Tag } from "@jmc/solid-design-system/src/components/atoms/Tag/Tag";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import classnames from "classnames";
import { FilterSelect } from "@jmc/solid-design-system/src/components/molecules/FilterSelect/FilterSelect";
import flatten from "lodash/flatten";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EventTypes } from "@jmc/core/src/types/EventTypes";

import style from "./style.module.scss";

interface PropTypes {
    id?: string;
    filter: FilterCategory[];
    onChangeFilter: (options: SelectedType | FilterSelectOptionType[], category?: FilterCategory) => void;
    onRemoveFilter: (option: FilterSelectOptionType) => void;
    onResetFilters?: () => void;
    sort?: FilterCategory;
    onChangeSort?: (option: FilterSelectOptionType) => void;
    canChangeViewOnMobile?: boolean;
    showLabelBelowFilter?: boolean;
}

export interface FilterCategory {
    id: string;
    name: string;
    options: FilterSelectOptionType[];
}

export const FilterControlPanel: React.FunctionComponent<PropTypes> = ({
    id = "ControlHeader",
    filter,
    sort,
    onChangeSort,
    onChangeFilter,
    onResetFilters,
    onRemoveFilter,
    canChangeViewOnMobile = false,
    showLabelBelowFilter,
}: PropTypes): JSX.Element => {
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState(false);
    const elementRef = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedFilters(
            filter?.filter((obj) => {
                return obj?.options?.find((f) => f.isSelected);
            }).length > 0,
        );
    }, [filter]);

    const productTagsEnabled =
        process.env.GATSBY_PRODUCT_TAG_RESTRICTION === "enabled" ||
        process.env.GATSBY_PRODUCT_TAG_RESTRICTION === "disabled";

    const filters = productTagsEnabled ? filter : filter?.filter((filterCategory) => filterCategory.name !== "Product");

    const handleResetKeyPress = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key === "Enter") {
            handleFilterReset();
        }
    };

    const handleMobileClose = (): void => {
        setFilterMenuOpen(false);
        elementRef.current.scrollIntoView({
            behavior: "instant",
            block: "center",
            inline: "start",
        });
    };

    const handleFilterChange = (
        data: FilterSelectOptionType,
        filterCategory: FilterCategory,
        filters: FilterCategory[],
    ): void => {
        document.dispatchEvent(
            new CustomEvent(EventTypes.FILTER_IMPRESSION, {
                bubbles: true,
                detail: { data: data, filterCategory: filterCategory, filters: filters },
            }),
        );
        onChangeFilter(filterCategory, data);
    };

    const handleFilterReset = (): void => {
        document.dispatchEvent(
            new CustomEvent(EventTypes.FILTER_RESET, {
                bubbles: true,
            }),
        );
        onResetFilters();
    };

    const totalSelectedCount =
        filters?.flatMap((category) => category.options)?.filter((option) => option?.isSelected)?.length || 0;

    const filtersCount = (filters?.length || 0) + (sort ? 1 : 0);

    return (
        <>
            <div className={style.controlHeader_desktop} data-test-id={`${id}.Desktop`}>
                <div
                    className={style.grid}
                    style={{
                        gridTemplateColumns: `${"minmax(max-content, 20%) ".repeat(filtersCount)}`,
                    }}
                >
                    {filters?.map((filterCategory, index) => (
                        <div className={classnames(style.filter)} key={filterCategory.name}>
                            <div
                                id={`filter-tag-${filterCategory.name}`}
                                className={style.filterField}
                                data-test-id={`ControlHeader.Filter.${filterCategory.name}`}
                            >
                                <FilterSelect
                                    id={`CustomFilter-${filterCategory.id}`}
                                    key={index}
                                    customClass={style["customFilter"]}
                                    placeholder={`${t(filterCategory?.name, { ns: "common" })}`}
                                    options={filterCategory?.options}
                                    isMulti={true}
                                    clickAction={(data) => {
                                        handleFilterChange(data, filterCategory, filters);
                                    }}
                                    relativePosition={false}
                                    invertedSelection={true}
                                />
                            </div>
                        </div>
                    ))}
                    {sort && (
                        <div className={classnames(style.filter, style.sort)}>
                            <div
                                id={`sort-by-${sort.name}`}
                                className={style.filterField}
                                data-test-id={`ControlHeader.Sort.${sort.name}`}
                            >
                                <FilterSelect
                                    id={sort.id}
                                    placeholder={sort.name}
                                    options={sort.options}
                                    clickAction={(data) => {
                                        onChangeSort(data);
                                    }}
                                    shouldShowMenuList={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={style.controlHeader_mobile} data-test-id={`${id}.Mobile`}>
                <div className={style.controlHeader_mobile_controls} ref={elementRef}>
                    <Button
                        variant="outlined"
                        color={"primary"}
                        size={"small"}
                        endIcon={null}
                        fullWidth={true}
                        onClick={() => {
                            setFilterMenuOpen(!filterMenuOpen);
                            window.scrollTo(0, 0);
                        }}
                    >
                        <Icon icon={jnjFilter} type="jnj" color="primary" />
                        {t("Filters", { ns: "common" })}
                        {totalSelectedCount > 0 ? ` (${totalSelectedCount})` : ""}
                    </Button>
                </div>

                {Boolean(filterMenuOpen) && (
                    <div className={style.controlHeader_mobile_filterMenu} data-test-id="filter-menu">
                        <div className={style.controlHeader_mobile_filterHeader}>
                            <div className={style.closeIcon}>
                                <Icon icon={jnjArrowLeft} type="jnj" color="black" onClick={handleMobileClose} />
                            </div>
                            <div className={style.filtersText}>
                                <Typography color={"dark"} size={"xl"} font="title">
                                    {t("Filters", { ns: "common" })}
                                </Typography>
                            </div>
                        </div>
                        {filters?.map((filterCategory, index) => {
                            const finalNumber =
                                filterCategory?.options.filter((option) => option.isSelected).length || 0;
                            return (
                                <div
                                    className={classnames(style.filter, canChangeViewOnMobile)}
                                    key={filterCategory.name}
                                >
                                    <div
                                        id={`filter-tag-${filterCategory.name}`}
                                        data-test-id={`ControlHeader.Filter.${filterCategory.name}`}
                                    >
                                        <FilterSelect
                                            id={`CustomFilter-${filterCategory.id}`}
                                            key={index}
                                            customClass={style["customFilter"]}
                                            placeholder={`${t(filterCategory?.name, { ns: "common" })}${finalNumber > 0 ? ` (${finalNumber})` : ""}`}
                                            options={filterCategory?.options}
                                            isMulti={true}
                                            clickAction={(data) => onChangeFilter(filterCategory, data)}
                                            relativePosition={true}
                                            invertedSelection={true}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                        {sort && (
                            <div className={classnames(style.filter, canChangeViewOnMobile)}>
                                <div id={`sort-by-${sort.name}`} data-test-id={`ControlHeader.Sort.${sort.name}`}>
                                    <FilterSelect
                                        id={sort.id}
                                        placeholder={sort.name}
                                        options={sort.options}
                                        clickAction={(data) => {
                                            onChangeSort(data);
                                        }}
                                        shouldShowMenuList={false}
                                        relativePosition={true}
                                        invertedSelection={true}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={style.actions}>
                            <Button
                                className={style.buttonActions}
                                variant="naked"
                                textVariant="label-01"
                                fullWidth
                                onClick={onResetFilters}
                                data-test-id="ControlHeader.ResetFilters"
                            >
                                {t("Clear filters", { ns: "common" })}
                            </Button>
                            <Button
                                className={style.buttonActions}
                                color="primary"
                                onClick={handleMobileClose}
                                fullWidth
                            >
                                {t("Show results", { ns: "common" })}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            <div className={style.filterTags}>
                {showLabelBelowFilter &&
                    flatten(filters?.map((category) => category?.options))
                        ?.filter((option) => option?.isSelected)
                        ?.map((option) => (
                            <Tag
                                color="primary"
                                data-test-id={`ControlHeader.Tags.Item.${option.value}`}
                                key={option.value}
                                dismissable
                                onClick={() => onRemoveFilter(option)}
                                enableHover={false} //Hover color grey disabled
                                blueHover // Hover color blue enabled
                            >
                                <Typography size={"xs"} weight="400" variant="span" color="white">
                                    {option.label && typeof option.label === "string" ? option.label : option.value}
                                </Typography>
                            </Tag>
                        ))}
                {onResetFilters && selectedFilters && (
                    <Button
                        className={style.clearFilters}
                        variant={"naked"}
                        color={"primary-500"}
                        noSpacing={true}
                        textVariant="label-01"
                        tabIndex={0}
                        onClick={() => {
                            handleFilterReset();
                        }}
                        onKeyDown={handleResetKeyPress}
                    >
                        {t("Clear filters", { ns: "common" })}
                    </Button>
                )}
            </div>
        </>
    );
};
